import { useState, useRef, useEffect, useCallback } from "react";
import "./MovableAndResizableComponent.css";

export default function MovableAndResizableComponent({
    children,
    initialX = 0,
    initialY = 0,
    initialWidth = 200,
    initialHeight = 100,
    minWidth = 50,
    minHeight = 50,
    onPositionChange,
    onSizeChange,
    onSelect,
    isSelected = false,
    cellId,
    cellType = "default",
    scale = 1
}) {
    const [position, setPosition] = useState({ x: initialX, y: initialY });
    const [size, setSize] = useState({ width: initialWidth, height: initialHeight });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [resizeHandle, setResizeHandle] = useState(null);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [initialPos, setInitialPos] = useState({ x: 0, y: 0 });
    const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });

    const componentRef = useRef(null);

    // Mise à jour si les props changent
    useEffect(() => {
        setPosition({ x: initialX, y: initialY });
    }, [initialX, initialY]);

    useEffect(() => {
        setSize({ width: initialWidth, height: initialHeight });
    }, [initialWidth, initialHeight]);

    // Gestion du drag
    const handleMouseDown = useCallback((e) => {
        if (e.target.classList.contains('resize-handle')) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
        setInitialPos({ x: position.x, y: position.y });
        
        onSelect?.(cellId);
    }, [position, cellId, onSelect]);

    // Gestion du resize
    const handleResizeMouseDown = useCallback((e, handle) => {
        e.preventDefault();
        e.stopPropagation();
        
        setIsResizing(true);
        setResizeHandle(handle);
        setDragStart({ x: e.clientX, y: e.clientY });
        setInitialPos({ x: position.x, y: position.y });
        setInitialSize({ width: size.width, height: size.height });
        
        onSelect?.(cellId);
    }, [position, size, cellId, onSelect]);

    // Refs pour stocker les dernières valeurs de position et size (pour éviter les problèmes de closure)
    const latestPositionRef = useRef(position);
    const latestSizeRef = useRef(size);

    // Mettre à jour les refs quand position/size changent
    useEffect(() => {
        latestPositionRef.current = position;
    }, [position]);

    useEffect(() => {
        latestSizeRef.current = size;
    }, [size]);

    // Gestion du mouvement de la souris
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging) {
                const deltaX = (e.clientX - dragStart.x) / scale;
                const deltaY = (e.clientY - dragStart.y) / scale;
                
                const newPos = {
                    x: Math.max(0, initialPos.x + deltaX),
                    y: Math.max(0, initialPos.y + deltaY)
                };
                
                setPosition(newPos);
            }
            
            if (isResizing && resizeHandle) {
                const deltaX = (e.clientX - dragStart.x) / scale;
                const deltaY = (e.clientY - dragStart.y) / scale;
                
                let newWidth = initialSize.width;
                let newHeight = initialSize.height;
                let newX = initialPos.x;
                let newY = initialPos.y;
                
                // Resize selon le handle
                if (resizeHandle.includes('e')) {
                    newWidth = Math.max(minWidth, initialSize.width + deltaX);
                }
                if (resizeHandle.includes('w')) {
                    const widthDelta = Math.min(deltaX, initialSize.width - minWidth);
                    newWidth = initialSize.width - widthDelta;
                    newX = initialPos.x + widthDelta;
                }
                if (resizeHandle.includes('s')) {
                    newHeight = Math.max(minHeight, initialSize.height + deltaY);
                }
                if (resizeHandle.includes('n')) {
                    const heightDelta = Math.min(deltaY, initialSize.height - minHeight);
                    newHeight = initialSize.height - heightDelta;
                    newY = initialPos.y + heightDelta;
                }
                
                setSize({ width: newWidth, height: newHeight });
                setPosition({ x: Math.max(0, newX), y: Math.max(0, newY) });
            }
        };

        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
                // Utiliser les refs pour avoir les valeurs actuelles
                onPositionChange?.(cellId, latestPositionRef.current);
            }
            if (isResizing) {
                setIsResizing(false);
                setResizeHandle(null);
                // Utiliser les refs pour avoir les valeurs actuelles
                // Passer aussi la position en cas de resize depuis les bords n/w
                onSizeChange?.(cellId, latestSizeRef.current, latestPositionRef.current);
            }
        };

        if (isDragging || isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, dragStart, initialPos, initialSize, resizeHandle, scale, minWidth, minHeight, cellId, onPositionChange, onSizeChange]);

    const handleClick = (e) => {
        e.stopPropagation();
        onSelect?.(cellId);
    };

    return (
        <div
            ref={componentRef}
            className={`movable-resizable ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                width: `${size.width}px`,
                height: `${size.height}px`,
            }}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
        >
            {/* Contenu */}
            <div className="movable-content">
                {children}
            </div>

            {/* Handles de resize (visibles uniquement si sélectionné) */}
            {isSelected && (
                <>
                    {/* Coins */}
                    <div 
                        className="resize-handle resize-handle-nw" 
                        onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
                    />
                    <div 
                        className="resize-handle resize-handle-ne" 
                        onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
                    />
                    <div 
                        className="resize-handle resize-handle-sw" 
                        onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
                    />
                    <div 
                        className="resize-handle resize-handle-se" 
                        onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
                    />
                    
                    {/* Côtés */}
                    <div 
                        className="resize-handle resize-handle-n" 
                        onMouseDown={(e) => handleResizeMouseDown(e, 'n')}
                    />
                    <div 
                        className="resize-handle resize-handle-s" 
                        onMouseDown={(e) => handleResizeMouseDown(e, 's')}
                    />
                    <div 
                        className="resize-handle resize-handle-w" 
                        onMouseDown={(e) => handleResizeMouseDown(e, 'w')}
                    />
                    <div 
                        className="resize-handle resize-handle-e" 
                        onMouseDown={(e) => handleResizeMouseDown(e, 'e')}
                    />

                    {/* Dimensions tooltip */}
                    <div className="size-tooltip">
                        {Math.round(size.width)} × {Math.round(size.height)}
                    </div>
                </>
            )}

            {/* Indicateur de type de cellule */}
            {isSelected && (
                <div className="cell-type-badge">
                    {getCellTypeIcon(cellType)} {cellType}
                </div>
            )}
        </div>
    );
}

function getCellTypeIcon(type) {
    const icons = {
        text: '📝',
        image: '🖼️',
        video: '🎬',
        button: '🔘',
        carousel: '🎠',
        form: '📋',
        iframe: '🌐',
        header: '📌',
        html: '💻',
        default: '📦'
    };
    return icons[type] || icons.default;
}

